import React, { useState, useEffect } from "react";
import { X, UserPlus } from "lucide-react";
import DeleteIcon from "@mui/icons-material/Delete";
import Cookies from "js-cookie";

const ManageCollaboratorsModal = ({
  isOpen,
  onClose,
  projectId,
  onCollaboratorsUpdated,
}) => {
  const [email, setEmail] = useState("");
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchExistingCollaborators();
    }
  }, [isOpen, projectId]);
  const fetchExistingCollaborators = async () => {
    try {
      const token = Cookies.get("token");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/project/listOfCollaboratorAllProject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({ project_id: projectId }),
        },
      );

      const data = await res.json();

      if (res.ok && data.success && data.data) {
        const list = data.data;

        const mapped = list.map((item) => ({
          id: item._id,
          email: item.email,
          permission: "Can View",
          status: item.isActive ? "active" : "pending",
        }));

        setCollaborators(mapped);
        onCollaboratorsUpdated?.(mapped);
      } else {
        setCollaborators([]);
      }
    } catch (err) {
      console.log(err);
      setCollaborators([]);
    }
  };
  const validateEmail = (email) => /^\S+@\S+\.\S+$/.test(email);

  const handleAddCollaborator = async () => {
    if (!email.trim()) return setError("Please enter an email address");
    if (!validateEmail(email)) return setError("Invalid email address");
    if (collaborators.some((c) => c.email === email.toLowerCase()))
      return setError("This collaborator already exists");

    setError("");
    setLoading(true);

    try {
      const token = Cookies.get("token");
      const emailString = email.toLowerCase();
      const addRes = await fetch(
        `${process.env.REACT_APP_API_URL}/Collaborator/addCollaboratortoProject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({
            projectId: projectId,
            email: emailString,
          }),
        },
      );

      const addData = await addRes.json();

      if (!addRes.ok || addData.success === false) {
        throw new Error(addData.message || "Failed to add collaborator");
      }
      const mailRes = await fetch(
        `${process.env.REACT_APP_API_URL}/collaborator/sendMailForAddCollaborator`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({
            projectId: projectId,
            email: emailString,
          }),
        },
      );

      const mailData = await mailRes.json();

      if (!mailRes.ok || mailData.success === false) {
        throw new Error(
          mailData.message || "Collaborator added but email failed",
        );
      }
      await fetchExistingCollaborators();

      setEmail("");
      setError("");
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCollaborator = async (id, collaboratorEmail) => {
    setRemovingId(id);
    try {
      const token = Cookies.get("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/collaborator/removecollaborator`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({
            projectId: projectId,
            email: collaboratorEmail,
          }),
        },
      );
      if (!res.ok) throw new Error("Failed to remove collaborator");
      setCollaborators((prev) => {
        const updated = prev.filter((c) => c.id !== id);
        onCollaboratorsUpdated?.(updated);
        return updated;
      });
    } catch (err) {
      console.error("Error removing collaborator:", err);
      setError(err.message || "Failed to remove collaborator");
    } finally {
      setRemovingId(null);
    }
  };

  const handleSaveChanges = () => {
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleAddCollaborator();
  };

  if (!isOpen) return null;

  return (
    <div className="collaborator-modal-overlay">
      <div className="collaborator-modal">
        <div className="collaborator-modal-header">
          <div>
            <h2 className="collaborator-modal-title">Manage Collaborators</h2>
            <p className="collaborator-modal-subtitle">
              Choose who you'd like to work with.
            </p>
          </div>
          <button onClick={onClose} className="collaborator-modal-close">
            <X size={24} />
          </button>
        </div>

        <div className="collaborator-modal-content">
          <div className="collaborator-input-section">
            <label className="collaborator-input-label">
              Input email addresses to invite people
            </label>

            <div className="collaborator-input-wrapper">
              <input
                type="email"
                value={email}
                disabled={loading}
                onKeyPress={handleKeyPress}
                placeholder="Type email"
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className="collaborator-input"
              />

              <button
                disabled={loading}
                onClick={handleAddCollaborator}
                className="collaborator-add-button"
              >
                <UserPlus size={20} /> {loading ? "Adding..." : "Add"}
              </button>
            </div>

            {error && <p className="collaborator-error">{error}</p>}
          </div>

          <div className="collaborator-list">
            {collaborators.length === 0 ? (
              <div className="collaborator-empty-state">
                <p>No collaborators added yet.</p>
              </div>
            ) : (
              collaborators.map((c) => (
                <div key={c.id} className="collaborator-item">
                  <div className="collaborator-item-info">
                    <div className="collaborator-avatar">
                      {(c.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="collaborator-text-content">
                      <p className="collaborator-email">{c.email}</p>

                      {c.status === "pending" && (
                        <span className="collaborator-status-badge">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="collaborator-item-actions">
                    <p className="collaborator-fixed-permission">Can View</p>

                    <button
                      onClick={() => handleRemoveCollaborator(c.id, c.email)}
                      className="collaborator-remove-button"
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="collaborator-modal-footer">
          <button onClick={onClose} className="collaborator-cancel-button">
            Cancel
          </button>

          <button
            onClick={handleSaveChanges}
            className="collaborator-save-button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageCollaboratorsModal;
